import React, { useState } from "react";
import { EyeOff, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { api } from "../lib/api";

const INCOGNITO_EMAIL = "info@sunprojects-gmbh.de";

/**
 * Inline toggle button for single-document incognito state.
 * Only renders if the currently logged-in user is the privileged Sunprojects admin.
 *
 * @param {object} user - the current user object from useAuth()
 * @param {string} docType - one of: offer, free_offer, wp_offer, legacy_offer, invoice, free_invoice, signing_request, credit_note
 * @param {string} docId - the document id
 * @param {boolean} active - current is_incognito flag
 * @param {() => void} onToggled - optional callback after successful toggle
 * @param {"sm"|"md"} size
 */
export default function IncognitoToggleButton({ user, docType, docId, active, onToggled, size = "sm" }) {
  const [busy, setBusy] = useState(false);
  const isPrivileged = (user?.email || "").trim().toLowerCase() === INCOGNITO_EMAIL;
  if (!isPrivileged) return null;

  const handleClick = async () => {
    const next = !active;
    if (next && !window.confirm("Dieses Dokument in den Inkognito-Modus versetzen?\n\nEs wird dann für alle anderen User unsichtbar.")) {
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/incognito/${docType}/${docId}`, { is_incognito: next });
      toast.success(next ? "Inkognito aktiviert" : "Inkognito deaktiviert");
      if (onToggled) onToggled();
    } catch (error) {
      console.error("Incognito toggle failed:", error);
      toast.error("Inkognito-Toggle fehlgeschlagen", { description: error?.response?.data?.detail || error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      variant={active ? "default" : "outline"}
      size={size}
      className={active ? "bg-purple-600 hover:bg-purple-700 text-white" : "border-purple-400 text-purple-700 hover:bg-purple-50"}
      onClick={handleClick}
      disabled={busy}
      data-testid="incognito-doc-toggle-btn"
      title="Inkognito-Modus für dieses Dokument umschalten"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <EyeOff className="h-4 w-4" />}
      <span className="ml-1.5 hidden sm:inline">
        {active ? "Inkognito AN" : "Inkognito"}
      </span>
    </Button>
  );
}
